import { RunContextApp } from "framework/RCApp";
import { toast } from '../utils/toast';

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: any;
}

export interface ErrorHandlingOptions {
  showToast?: boolean;
  showAlert?: boolean;
  context?: string;
  fallbackMessage?: string;
}

export class CustomerDataErrorHandler {
 
  static handleApiError(
    error: any, 
    options: ErrorHandlingOptions = {}
  ): string {
    const {
      showToast = true,
      showAlert = true,
      context = 'customer data operation',
      fallbackMessage = 'An unexpected error occurred'
    } = options;

    console.error(`${context} error:`, error);
    
    let errorMessage = fallbackMessage;
    let errorType: 'error' | 'warning' = 'error';
    
    if (error && typeof error === 'object') {
      if (error.message) {
        errorMessage = error.message;
      } else if (error.error && error.error.message) {
        errorMessage = error.error.message;
      } else if (error.details) {
        errorMessage = Array.isArray(error.details) 
          ? error.details.join(', ')
          : error.details;
      }
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    const categorizedError = this.categorizeError(errorMessage);
    errorMessage = categorizedError.message;
    errorType = categorizedError.type;

    if (showToast) {
      this.showToast(errorMessage, errorType);
    }

    return errorMessage;
  }

  private static categorizeError(message: string): { message: string; type: 'error' | 'warning' } {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('already exists') || 
        lowerMessage.includes('duplicate') || 
        lowerMessage.includes('conflict')) {
      return {
        message: `Operation failed: ${message}. Please try with different parameters.`,
        type: 'warning'
      };
    }

    if (lowerMessage.includes('validation') || 
        lowerMessage.includes('invalid') || 
        lowerMessage.includes('required') ||
        lowerMessage.includes('format')) {
      return {
        message: `Validation error: ${message}`,
        type: 'warning'
      };
    }

    if (lowerMessage.includes('permission') || 
        lowerMessage.includes('unauthorized') || 
        lowerMessage.includes('forbidden') ||
        lowerMessage.includes('access denied')) {
      return {
        message: `Access denied: ${message}`,
        type: 'error'
      };
    }

    if (lowerMessage.includes('network') || 
        lowerMessage.includes('connection') || 
        lowerMessage.includes('fetch')) {
      return {
        message: 'Network error: Please check your connection and try again.',
        type: 'error'
      };
    }

    if (lowerMessage.includes('timeout') || 
        lowerMessage.includes('timed out')) {
      return {
        message: 'Request timeout: Please try again.',
        type: 'warning'
      };
    }

    if (lowerMessage.includes('server error') || 
        lowerMessage.includes('internal error') ||
        lowerMessage.includes('500')) {
      return {
        message: 'Server error: Please try again later.',
        type: 'error'
      };
    }

    if (lowerMessage.includes('not found') || 
        lowerMessage.includes('404')) {
      return {
        message: `Resource not found: ${message}`,
        type: 'warning'
      };
    }

    if (lowerMessage.includes('rate limit') || 
        lowerMessage.includes('too many requests')) {
      return {
        message: 'Too many requests: Please wait a moment and try again.',
        type: 'warning'
      };
    }

    if (lowerMessage.includes('user not found') || 
        lowerMessage.includes('no users found')) {
      return {
        message: `Customer data error: ${message}`,
        type: 'warning'
      };
    }

    if (lowerMessage.includes('rate history') || 
        lowerMessage.includes('transition history')) {
      return {
        message: `Rate history error: ${message}`,
        type: 'warning'
      };
    }

    return {
      message: message,
      type: 'error'
    };
  }

  private static showToast(message: string, type: 'error' | 'warning'): void {
    switch (type) {
      case 'warning':
        toast.warning(message);
        break;
      default:
        toast.error(message);
        break;
    }
  }

  static handleRateHistoryError(error: any, operation: 'fetch' | 'create' | 'update' = 'fetch'): string {
    const context = `Rate history ${operation}`;
    const fallbackMessage = `Rate history ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }


  static handleUserMetadataError(error: any, operation: 'fetch' | 'create' | 'update' | 'delete' = 'fetch'): string {
    const context = `User metadata ${operation}`;
    const fallbackMessage = `User metadata ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }


  static handleDataGenerationError(error: any, operation: 'generate' | 'fetch' | 'process' = 'generate'): string {
    const context = `Customer data ${operation}`;
    const fallbackMessage = `Customer data ${operation} failed. Please try again.`;
    
    return this.handleApiError(error, {
      context,
      fallbackMessage,
      showToast: true
    });
  }
}

export const handleCustomerDataApiError = (error: any, context?: string): string => {
  return CustomerDataErrorHandler.handleApiError(error, { context });
};


export const CustomerDataApis = {

  fetchRateHistory: async (rc: RunContextApp, requestData: {
    uuid?: string;
    utility_name?: string;
    source_uuid?: string;
    source_env?: string;
    destination_pilot_id?: string;
    source_pilot_id?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchRateHistory(requestData);

      if ('error' in response && response.error) {
        const errorMessage = CustomerDataErrorHandler.handleRateHistoryError(response.error, 'fetch');
        return {
          success: false,
          error: errorMessage
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      const errorMessage = CustomerDataErrorHandler.handleRateHistoryError(error, 'fetch');
      rc.error(rc.getName(CustomerDataApis), 
        `Error >> GET request to : /v1/fetch-rate-history\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: errorMessage
      };
    }
  },


  fetchDataSchedulerHistory: async (rc: RunContextApp, requestData: {
    destination_pilot_id: string;
    job_name: string;
    uuid: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchDataSchedulerHistory(requestData);

      if ('error' in response && response.error) {
        const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(response.error, 'fetch');
        return {
          success: false,
          error: errorMessage
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(error, 'fetch');
      rc.error(rc.getName(CustomerDataApis), 
        `Error >> GET request to : /v1/data-scheduler-history\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: errorMessage
      };
    }
  },
  

  fetchConsumptionData: async (rc: RunContextApp, requestData: {
    uuid: string;
    destinationBaseUrl: string;
    meterFuel?: string;
    destinationPilotId?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchConsumptionData(requestData);

      if ('error' in response && response.error) {
        const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(response.error, 'fetch');
        return {
          success: false,
          error: errorMessage
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(error, 'fetch');
      rc.error(rc.getName(CustomerDataApis), 
        `Error >> GET request to : /v1/consumption-data\n` + 
        `\nresulted in error : \n`, error);
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  fetchUserAttribute: async (rc: RunContextApp, requestData: {
    uuid?: string;
    destination_pilot_id: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchUserAttribute(requestData);

      if ('error' in response && response.error) {
        const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(response.error, 'fetch');
        return { success: false, error: errorMessage };
      }

      if ('data' in response && response.data) {
        return { success: true, data: response.data };
      }

      return { success: true, data: undefined };
    } catch (error) {
      const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(error, 'fetch');
      rc.error(rc.getName(CustomerDataApis),
        `Error >> GET request to : /v1/fetch-user-attribute\n` +
        `\nresulted in error : \n`, error);
      return { success: false, error: errorMessage };
    }
  },

  fetchUserDataSchedulerHistory: async (rc: RunContextApp, requestData: {
    destination_pilot_id: string;
    uuid: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchUserDataSchedulerHistory(requestData);
      if ('error' in response && response.error) {
        const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(response.error, 'fetch');
        return { success: false, error: errorMessage };
      }
      if ('data' in response && response.data) {
        return { success: true, data: response.data };
      }
      return { success: true, data: undefined };
    } catch (error) {
      const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(error, 'fetch');
      rc.error(rc.getName(CustomerDataApis),
        `Error >> GET request to : /v1/data-scheduler-history\n` +
        `\nresulted in error : \n`, error);
      return { success: false, error: errorMessage };
    }
  },

 assignPhysicalCluster: async (rc: RunContextApp, requestData: {
   uuid: string;
   destination_pilot_id: string;
 }): Promise<{ success: boolean; data?: any; error?: string }> => {
   try {
     const response = await rc.bffClient.assignPhysicalCluster(requestData);

     if ('error' in response && response.error) {
       const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(response.error, 'fetch');
       return { success: false, error: errorMessage };
     }

     if ('data' in response && response.data) {
       return { success: true, data: response.data };
     }

     return { success: true, data: undefined };
   } catch (error) {
     const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(error, 'fetch');
     rc.error(rc.getName(CustomerDataApis),
       `Error >> POST request to : /v1/assign-physical-cluster\n` +
       `\nresulted in error : \n`, error);
     return { success: false, error: errorMessage };
   }
  },


  fetchBBUserProgram: async (rc: RunContextApp, requestData: {
    userUUID: string;
    utilityName: string;
    sourceEnvironment: string;
    destinationPilotId: string;
    utilityId: string;
    hasBBUserProgram: boolean;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.fetchBBUserProgram(requestData);

      if ('error' in response && response.error) {
        const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(response.error, 'fetch');
        return {
          success: false,
          error: errorMessage
        };
      }

      if ('data' in response && response.data) {
        if (response.data.success === false) {
          const errorMessage = response.data.errorDetails || response.data.message || 'BB User Program generation failed';
          return {
            success: false,
            data: response.data,
            error: errorMessage
          };
        }
        
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(error, 'fetch');
      rc.error(rc.getName(CustomerDataApis), 
        `Error >> GET request to : /v1/fetchProdUserData/BBUserProgram\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: errorMessage
      };
    }
  },


  updateSecondaryEmail: async (rc: RunContextApp, requestData: {
    uuid: string;
    destination_pilot_id: string;
    secondary_email: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.updateSecondaryEmail(requestData);

      if ('error' in response && response.error) {
        const errorMessage = CustomerDataErrorHandler.handleUserMetadataError(response.error, 'update');
        return {
          success: false,
          error: errorMessage
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      const errorMessage = CustomerDataErrorHandler.handleUserMetadataError(error, 'update');
      rc.error(rc.getName(CustomerDataApis),
        `Error >> POST request to : /v1/update-secondary-email\n` +
        `\nresulted in error : \n`, error);
      return {
        success: false,
        error: errorMessage
      };
    }
  },


  updateNameEmail: async (rc: RunContextApp, requestData: {
    uuid: string;
    destination_pilot_id: string;
    first_name: string;
    last_name?: string;
    primary_email: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.updateNameEmail(requestData);

      if ('error' in response && response.error) {
        const errorMessage = CustomerDataErrorHandler.handleUserMetadataError(response.error, 'update');
        return {
          success: false,
          error: errorMessage
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      const errorMessage = CustomerDataErrorHandler.handleUserMetadataError(error, 'update');
      rc.error(rc.getName(CustomerDataApis),
        `Error >> POST request to : /v1/update-name-email\n` +
        `\nresulted in error : \n`, error);
      return {
        success: false,
        error: errorMessage
      };
    }
  },


  updateAddressZipcode: async (rc: RunContextApp, requestData: {
    uuid: string;
    destination_pilot_id: string;
    address: string;
    zipcode: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.updateAddressZipcode(requestData);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }

      if ('data' in response && response.data) {
        // Check if response contains error status
        if (response.data.status === 'error' && response.data.message) {
          return {
            success: false,
            data: response.data,
            error: response.data.message
          };
        }
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error: any) {
      // Extract error message from error response
      let errorMessage = 'Failed to update address and zipcode';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      rc.error(rc.getName(CustomerDataApis),
        `Error >> POST request to : /v1/update-address-zipcode\n` +
        `\nresulted in error : \n`, error);
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  rateTransition: async (rc: RunContextApp, requestData: {
    uuid: string;
    destination_pilot_id: string;
    rate_plan: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.rateTransition(requestData);

      if ('error' in response && response.error) {
        const errorMessage = CustomerDataErrorHandler.handleUserMetadataError(response.error, 'update');
        return {
          success: false,
          error: errorMessage
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      const errorMessage = CustomerDataErrorHandler.handleUserMetadataError(error, 'update');
      rc.error(rc.getName(CustomerDataApis),
        `Error >> POST request to : /v1/rate-transition\n` +
        `\nresulted in error : \n`, error);
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  syncWeatherData: async (rc: RunContextApp, requestData: {
    source_uuid: string[];
    source_environment: string;
    destinationEnvironment: string;
    destination_pilot_id: string;
    sourceCountry?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await rc.bffClient.syncWeatherData(requestData);

      if ('error' in response && response.error) {
        const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(response.error, 'process');
        return {
          success: false,
          error: errorMessage
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      const errorMessage = CustomerDataErrorHandler.handleDataGenerationError(error, 'process');
      rc.error(rc.getName(CustomerDataApis),
        `Error >> POST request to : /v1/fetchProdUserData/syncWeatherData\n` +
        `\nresulted in error : \n`, error);
      return {
        success: false,
        error: errorMessage
      };
    }
  }
};
